import useCreateEstimate from '@/api/mutations/estimates/useCreateEstimate';
import useGetProjectEstimates from '@/api/queries/project/useGetProjectEstimates';
import useGetProjectHeatLossEstimate from '@/api/queries/project/useGetProjectHeatLossEstimate';
import { Button, Card, SkeletonLoader } from '@payaca/ambrosia';
import { FC } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import HeatLossAssumptionsAlert from '../HeatLossAssumptionsAlert';
import ArchivedEstimatesSection from './ArchivedEstimatesSection';
import EstimateCard from './EstimateCard';

const SummaryTile: FC<{ title: string; value: string }> = (props) => {
  const { title, value } = props;
  return (
    <div className="px-3 py-1.5 text-center">
      <p className="supporting-body text-gray-500">{title}</p>
      <p className="font-semibold">{value}</p>
    </div>
  );
};

const EstimatesPanel: FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const history = useHistory();
  const { heatLossEstimate } = useGetProjectHeatLossEstimate(projectId);
  const { project, isLoading } = useGetProjectEstimates(projectId);
  const { mutate: createEstimate, isPending: isCreatingEstimate } =
    useCreateEstimate({
      onSuccess: (data, variables) => {
        history.push(
          `/projects/${variables.projectId}/estimates/${data.createEstimate.id}`
        );
      },
    });

  const estimates = project?.estimates ?? [];
  const activeEstimates = estimates.filter((e) => e.status !== 'ARCHIVED');
  const archivedEstimates = estimates.filter((e) => e.status === 'ARCHIVED');

  if (isLoading) {
    return (
      <div className="@container">
        <Card>
          <Card.Body className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <SkeletonLoader className="h-16 w-72 rounded-md" />
              <SkeletonLoader className="h-8 w-32 rounded-md" />
            </div>
            <ul aria-label="Estimates" className="list-none p-0 space-y-4">
              {[0, 1].map((i) => (
                <li key={i}>
                  <SkeletonLoader className="h-40 w-full rounded-md" />
                </li>
              ))}
            </ul>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="@container">
      <Card>
        <Card.Body className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            {heatLossEstimate ? (
              <div className="inline-flex flex-wrap items-stretch divide-x divide-gray-200 rounded-md border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
                <SummaryTile
                  title="Peak heat loss"
                  value={`${heatLossEstimate.peakHeatLoad.toFixed(2)}kW`}
                />
                <SummaryTile
                  title="Heat pump size"
                  value={`${heatLossEstimate.heatPumpSizeRange.lowerKw}–${heatLossEstimate.heatPumpSizeRange.upperKw}kW`}
                />
                {heatLossEstimate.minimumCylinderLitres != null && (
                  <SummaryTile
                    title="Minimum cylinder size"
                    value={`${heatLossEstimate.minimumCylinderLitres}L`}
                  />
                )}
              </div>
            ) : (
              <p className="self-center">
                A heat loss estimate is not available for this Project.
              </p>
            )}
            <Button
              variant={activeEstimates.length === 0 ? 'solid' : 'white'}
              onClick={() =>
                createEstimate({
                  projectId,
                  // TODO: support selecting categories — hardcoded to HEAT_PUMP for now
                  categories: ['HEAT_PUMP'],
                })
              }
              isProcessing={isCreatingEstimate}
              disabled={isCreatingEstimate}
              size="sm"
            >
              Create estimate
            </Button>
          </div>
          {heatLossEstimate && (
            <HeatLossAssumptionsAlert
              method={heatLossEstimate.method}
              targetIndoorTemperatureC={
                heatLossEstimate.targetIndoorTemperatureC
              }
            />
          )}

          <ul aria-label="Estimates" className="list-none p-0 space-y-4">
            {activeEstimates.map((estimate) => (
              <li key={estimate.id}>
                <EstimateCard projectId={projectId} estimate={estimate} />
              </li>
            ))}
          </ul>
          <ArchivedEstimatesSection
            projectId={projectId}
            estimates={archivedEstimates}
          />
        </Card.Body>
      </Card>
    </div>
  );
};

export default EstimatesPanel;
